'use client';

import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';
import { User } from 'firebase/auth';
import { Auth } from '@/integrations/firebase/config';
import { authService } from '@/integrations/firebase/authService';
import { useNavigate } from 'react-router-dom';

// Auth token constants
const TOKEN_REFRESH_INTERVAL = 50 * 60 * 1000; // 50 minutes
const SESSION_COOKIE_NAME = 'session';
const SESSION_MAX_AGE = 3600; // 1 hour in seconds

interface AuthContextType {
  user: User | null;
  loading: boolean;
  signOut: () => Promise<void>;
  refreshToken: () => Promise<string | null>;
}

const defaultContext: AuthContextType = {
  user: null,
  loading: true,
  signOut: async () => { throw new Error('AuthContext not initialized'); },
  refreshToken: async () => { throw new Error('AuthContext not initialized'); }
};

export const AuthContext = createContext<AuthContextType>(defaultContext);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [tokenRefreshAttempts, setTokenRefreshAttempts] = useState(0);
  const navigate = useNavigate();

  // Force token refresh with exponential backoff on failure
  const refreshToken = useCallback(async (): Promise<string | null> => {
    if (!user) return null;
    
    try {
      setTokenRefreshAttempts(prev => prev + 1);
      const token = await user.getIdToken(true);
      
      // Set secure cookie
      setCookie(SESSION_COOKIE_NAME, token, SESSION_MAX_AGE);
      
      // Reset attempts on success
      setTokenRefreshAttempts(0);
      return token;
    } catch (error) {
      console.error('Token refresh failed:', error);
      
      // If we've tried more than 3 times, give up for now
      if (tokenRefreshAttempts > 3) {
        console.error('Too many token refresh failures, giving up');
        setTokenRefreshAttempts(0);
        return null;
      }
      
      return null;
    }
  }, [user, tokenRefreshAttempts]);

  // Helper function to set cookies with security attributes
  const setCookie = (name: string, value: string, maxAgeSeconds: number) => {
    if (typeof document === 'undefined') return; // Client-side guard
    
    // Note: HttpOnly cannot be set from client-side JavaScript, only from server
    document.cookie = `${name}=${value}; path=/; max-age=${maxAgeSeconds}; SameSite=Strict; ${
      window.location.protocol === 'https:' ? 'Secure;' : ''
    }`;
  };

  // Helper function to delete cookies
  const deleteCookie = (name: string) => {
    if (typeof document === 'undefined') return; // Client-side guard
    
    document.cookie = `${name}=; path=/; expires=Thu, 01 Jan 1970 00:00:01 GMT`;
  };
  
  // Set up token refresh interval to handle token expiration
  useEffect(() => {
    let refreshInterval: NodeJS.Timeout | null = null;
    
    if (user) {
      // Get initial token
      user.getIdToken().then(token => {
        setCookie(SESSION_COOKIE_NAME, token, SESSION_MAX_AGE);
      }).catch(error => {
        console.error('Initial token retrieval failed:', error);
      });
      
      // Refresh the token every 50 minutes (Firebase tokens last 60 minutes)
      refreshInterval = setInterval(async () => {
        await refreshToken();
      }, TOKEN_REFRESH_INTERVAL);
    }
    
    return () => {
      if (refreshInterval) {
        clearInterval(refreshInterval);
      }
    };
  }, [user, refreshToken]);
  
  useEffect(() => {
    let mounted = true;
    let unsubscribe = () => {};

    const initAuth = async () => {
      try {
        setLoading(true);
        
        unsubscribe = Auth.onAuthStateChanged(async (firebaseUser) => {
          if (!mounted) return;
          
          try {
            if (firebaseUser) {
              // Get a token if we don't have one
              const token = await firebaseUser.getIdToken();
              setCookie(SESSION_COOKIE_NAME, token, SESSION_MAX_AGE);
              setUser(firebaseUser);
            } else {
              setUser(null);
              deleteCookie(SESSION_COOKIE_NAME);
            }
          } catch (error) {
            console.error('Auth state change error:', error);
            setUser(null);
            deleteCookie(SESSION_COOKIE_NAME);
          } finally {
            if (mounted) setLoading(false);
          }
        });
      } catch (error) {
        console.error('Auth initialization error:', error);
        if (mounted) setLoading(false);
      }
    };

    initAuth();

    return () => {
      mounted = false;
      unsubscribe();
    };
  }, []);

  const handleSignOut = useCallback(async () => {
    try {
      setLoading(true);
      await authService.signOut();
      
      // Clear session cookie
      deleteCookie(SESSION_COOKIE_NAME);
      
      // Redirect to auth page
      navigate('/auth');
    } catch (error) {
      console.error('Error during sign out:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  // Memoize context value to prevent unnecessary re-renders
  const authValue = useMemo(() => ({
    user, 
    loading,
    signOut: handleSignOut,
    refreshToken
  }), [user, loading, handleSignOut, refreshToken]);

  return (
    <AuthContext.Provider value={authValue}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
